/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, css, cssMap, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { Flex, Inline, Pressable, Text, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { Suggestion } from '../../../aiSuggestionsPluginType';

import { SuggestionAgentIdentity } from './SuggestionAgentIdentity';
import { SuggestionCardSources } from './SuggestionCardSources';

export const SUGGESTION_CARD_TESTID = 'ai-suggestion-card';

type SuggestionCardProps = {
	/**
	 * Optional banner (e.g. `<StatusBanner status="stale" />`) shown above the
	 * short description. `SuggestionCard` doesn't know what a banner is or
	 * when to show one — the caller decides and passes the already-built
	 * element.
	 */
	banner?: React.ReactNode;
	isHidingDeletedDiffs?: boolean;
	/**
	 * The selected suggestion is being regenerated (stale check → regenerate).
	 * While true, the action buttons are disabled so the user can't act on
	 * about-to-be-replaced content.
	 */
	isRegenerating?: boolean;
	onAccept?: () => void;
	onBadSuggestion?: () => void;
	onClose?: () => void;
	onDiscard?: () => void;
	onDismiss?: () => void;
	onGoodSuggestion?: () => void;
	onNavigate?: (direction: 'up' | 'down') => void;
	onReasoningViewed?: () => void;
	onShowSidebar?: () => void;
	onToggleDeletedDiffs?: () => void;
	suggestion: Suggestion;
};

// Shimmer applied to the description while the suggestion is regenerating.
// Copied from rovo-platform-ui-components' ShimmerText so we don't couple to it.
const shimmerAnimation = keyframes({
	'0%': {
		backgroundPosition: '200% 0',
	},
	'100%': {
		backgroundPosition: '-200% 0',
	},
});

const shimmerGradient = css({
	background: `linear-gradient(
		90deg,
		${token('color.text.subtlest')} 0%,
		${token('color.text.subtlest')} 20%,
		#c7cddc 40%,
		${token('color.text.subtlest')} 60%,
		${token('color.text.subtlest')} 80%,
		#c7cddc 90%,
		${token('color.text.subtlest')} 100%
	)`,
	backgroundSize: '200% 100%',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	animation: `${shimmerAnimation} 4s linear infinite`,
});

const styles = cssMap({
	sourceButton: {
		display: 'flex',
		backgroundColor: 'transparent',
		alignItems: 'center',
		paddingRight: token('space.0'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.0'),
		gap: token('space.075'),
		color: token('color.text.subtle'),
		'&:disabled': {
			color: token('color.text.disabled'),
		},
	},
	card: {
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		boxShadow: token('elevation.shadow.overlay'),
		// Kept in sync with `CARD_WIDTH_FALLBACK` in `useSuggestionCardPositioning.ts`,
		// which uses it to decide whether the card fits to the right of its target
		// before the card has been measured.
		width: '280px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		boxSizing: 'border-box',
	},
	cardLayout: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	privateMetadata: {
		flexShrink: 0,
	},
});

export const SuggestionCard: React.ForwardRefExoticComponent<
	SuggestionCardProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, SuggestionCardProps>(function SuggestionCard(
	{
		banner,
		onAccept,
		onDiscard,
		onDismiss,
		onGoodSuggestion,
		onBadSuggestion,
		onNavigate,
		onReasoningViewed,
		onClose,
		onShowSidebar,
		onToggleDeletedDiffs,
		isHidingDeletedDiffs,
		isRegenerating = false,
		suggestion,
	},
	ref,
): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isSourcesExpanded, setIsSourcesExpanded] = React.useState(false);

	const handleSourcesToggle = useCallback(() => {
		if (!isSourcesExpanded) {
			onReasoningViewed?.();
		}
		setIsSourcesExpanded((previous) => !previous);
	}, [isSourcesExpanded, onReasoningViewed]);

	const renderMoreOptionsTrigger = useCallback(
		({
			triggerRef,
			'aria-controls': ariaControls,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHasPopup,
			onClick,
			isSelected,
		}: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				ref={triggerRef}
				aria-controls={ariaControls}
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHasPopup}
				isDisabled={isRegenerating}
				isSelected={isSelected}
				onClick={onClick}
				icon={ShowMoreHorizontalIcon}
				label={formatMessage(aiSuggestionsMessages.cardMoreOptionsButtonLabel)}
				appearance="subtle"
				spacing="default"
			/>
		),
		[formatMessage, isRegenerating],
	);

	const ChevronUpSmall = useCallback(() => {
		return (
			<ChevronUpIcon
				label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
				size="small"
			/>
		);
	}, [formatMessage]);

	const ChevronDownSmall = useCallback(() => {
		return (
			<ChevronDownIcon
				label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
				size="small"
			/>
		);
	}, [formatMessage]);

	const isInsight = suggestion.actionKind === 'INFORMATIONAL';
	// An out-of-date suggestion can no longer be safely applied — only discard.
	const isStale = suggestion.status === 'STALE';

	return (
		<div
			ref={ref}
			css={[styles.card]}
			data-testid={SUGGESTION_CARD_TESTID}
			data-suggestion-id={suggestion.id}
		>
			<Flex alignItems="center" justifyContent="space-between">
				<Inline space="space.0" alignBlock="center">
					<IconButton
						icon={ChevronUpSmall}
						label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('up') : undefined}
					/>
					<IconButton
						icon={ChevronDownSmall}
						label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('down') : undefined}
					/>
				</Inline>
				<Inline space="space.050" alignBlock="center">
					{onShowSidebar && (
						<IconButton
							icon={PanelRightIcon}
							label={formatMessage(aiSuggestionsMessages.cardShowSidebarButtonLabel)}
							appearance="subtle"
							spacing="compact"
							onClick={onShowSidebar}
						/>
					)}
					<IconButton
						icon={CrossIcon}
						label={formatMessage(aiSuggestionsMessages.cardCloseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						onClick={onClose}
					/>
				</Inline>
			</Flex>

			{banner}
			<div css={[styles.cardLayout]}>
				<Text size="large" weight="medium">
					{suggestion.shortDescription}
				</Text>
				<Inline space="space.075" alignBlock="center" separator="·">
					<SuggestionAgentIdentity agent={suggestion.createdByAgent} />
					<div css={styles.privateMetadata}>
						<Tooltip
							position="top"
							content={formatMessage(aiSuggestionsMessages.cardPrivateTooltipLabelShort)}
							ignoreTooltipPointerEvents
						>
							<Inline as="span" space="space.050" alignBlock="center">
								<LockLockedIcon label="" size="small" color={token('color.icon.subtle')} />
								<Text size="small" weight="regular" color="color.text.subtle" align="center">
									{formatMessage(aiSuggestionsMessages.cardPrivateLabel)}
								</Text>
							</Inline>
						</Tooltip>
					</div>
				</Inline>
			</div>
			<Stack space="space.100">
				{isRegenerating ? (
					<div css={shimmerGradient}>
						<Text as="p" color={isRegenerating ? 'color.text.disabled' : 'color.text.subtlest'}>
							{suggestion.description}
						</Text>
					</div>
				) : (
					<Text as="p" color="color.text">
						{suggestion.description}
					</Text>
				)}
				{suggestion.reasoning && (
					<Flex alignItems="start" direction="column" gap="space.100">
						<Pressable
							xcss={styles.sourceButton}
							onClick={handleSourcesToggle}
							isDisabled={isRegenerating}
						>
							<Text size="small" weight="regular" color="inherit">
								{isSourcesExpanded
									? formatMessage(aiSuggestionsMessages.cardSourcesHideButtonLabel)
									: formatMessage(aiSuggestionsMessages.cardSourcesShowButtonLabel)}
							</Text>
							{isSourcesExpanded ? (
								<ChevronDownIcon label="" size="small" />
							) : (
								<ChevronRightIcon label="" size="small" />
							)}
						</Pressable>
						{isSourcesExpanded && (
							<Text as="p" color="color.text.subtle">
								{suggestion.reasoning}
							</Text>
						)}
						{!!suggestion?.sources?.length && (
							<SuggestionCardSources sources={suggestion.sources} />
						)}
					</Flex>
				)}
			</Stack>
			<Flex alignItems="center" justifyContent="space-between" gap="space.100">
				<Inline space="space.050" alignBlock="center">
					{isInsight ? (
						<Button onClick={onDismiss} isDisabled={isRegenerating}>
							{formatMessage(aiSuggestionsMessages.cardDismissButtonLabel)}
						</Button>
					) : (
						<React.Fragment>
							{/* A stale suggestion can only be discarded — hide Accept. */}
							{!isStale && (
								<Button appearance="primary" onClick={onAccept} isDisabled={isRegenerating}>
									{formatMessage(aiSuggestionsMessages.cardAcceptButtonLabel)}
								</Button>
							)}
							<Button
								appearance={isStale ? 'primary' : 'subtle'}
								onClick={onDiscard}
								isDisabled={isRegenerating}
							>
								{formatMessage(aiSuggestionsMessages.cardDiscardButtonLabel)}
							</Button>
						</React.Fragment>
					)}
				</Inline>
				{!isInsight && (onToggleDeletedDiffs || onGoodSuggestion || onBadSuggestion) && (
					<DropdownMenu<HTMLButtonElement>
						placement="bottom-end"
						trigger={renderMoreOptionsTrigger}
					>
						<DropdownItemGroup>
							{onToggleDeletedDiffs && (
								<DropdownItem
									elemBefore={
										isHidingDeletedDiffs ? (
											<EyeOpenStrikethroughIcon label="" />
										) : (
											<EyeOpenIcon label="" />
										)
									}
									onClick={onToggleDeletedDiffs}
								>
									{isHidingDeletedDiffs
										? formatMessage(aiSuggestionsMessages.cardShowDeletedDiffsLabel)
										: formatMessage(aiSuggestionsMessages.cardHideDeletedDiffsLabel)}
								</DropdownItem>
							)}
							{onGoodSuggestion && (
								<DropdownItem elemBefore={<ThumbsUpIcon label="" />} onClick={onGoodSuggestion}>
									{formatMessage(aiSuggestionsMessages.cardGoodSuggestionLabel)}
								</DropdownItem>
							)}
							{onBadSuggestion && (
								<DropdownItem elemBefore={<ThumbsDownIcon label="" />} onClick={onBadSuggestion}>
									{formatMessage(aiSuggestionsMessages.cardBadSuggestionLabel)}
								</DropdownItem>
							)}
						</DropdownItemGroup>
					</DropdownMenu>
				)}
			</Flex>
		</div>
	);
});
